import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Source, Layer, Popup } from 'react-map-gl';
import { parseISO, addDays, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import ContrailFrame from './ContrailFrame';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const MAX_CACHED_HOURS = 6;

// Utility function for formatting dates in UTC
const formatDateUTC = (date) => {
    return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
};

const cleanupCache = (cache, currentDateTime) => {
    const keys = Object.keys(cache);
    if (keys.length <= MAX_CACHED_HOURS) return;
    const currentHour = currentDateTime.getUTCHours();
    const currentDateStr = formatDateUTC(currentDateTime);
    const keyScores = keys.map(key => {
        const parts = key.split('_');
        const dateStr = parts[1];
        const hour = parseInt(parts[2]);
        let score = dateStr !== currentDateStr ? 100 : Math.abs(hour - currentHour);
        return { key, score };
    });
    keyScores.sort((a, b) => b.score - a.score);
    keyScores.slice(0, keys.length - MAX_CACHED_HOURS).forEach(item => delete cache[item.key]);
};

const GmnContrailLayer = ({ 
    layerId, 
    visible, 
    currentDateTime, 
    addCachedHour, 
    cachedHours, 
    isPlaying, 
    selectedContrail
}) => {
    const [contrailDetections, setContrailDetections] = useState([]);
    const [filteredDetections, setFilteredDetections] = useState([]);
    const [popupInfo, setPopupInfo] = useState(null);
    const dataCache = useRef({});
    const inProgressCache = useRef(new Set());

    const isDataEmpty = (data) => {
        if (Array.isArray(data)) {
            return data.length === 0;
        }
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data).length === 0;
        }
        return !data; // This will catch null, undefined, '', 0, false
    };

    const fetchHourData = useCallback(async (layerId, dateStr, hour) => {
        const cacheKey = `${layerId}_${dateStr}_${hour}`;
        if (dataCache.current[cacheKey] || inProgressCache.current.has(cacheKey)) {
            return dataCache.current[cacheKey] || null;
        }

        inProgressCache.current.add(cacheKey);
        try {
            // Fetch contrail detections from ground cameras
            const response = await fetch(`${API_BASE_URL}/gmn_detected?date_str=${dateStr}&hour=${hour}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const data = await response.json();

            if (!isDataEmpty(data)) {
                dataCache.current[cacheKey] = data;
                addCachedHour(layerId, parseISO(`${dateStr}T${hour.toString().padStart(2, '0')}:00:00Z`), hour);
                return data;
            } else {
                return null;
            }
        } catch (error) {
            console.error(`Error fetching GMN detected contrails for ${cacheKey}:`, error);
            return null;
        } finally {
            inProgressCache.current.delete(cacheKey);
        }
    }, [addCachedHour]);

    const fetchRelevantData = useCallback(async () => {
        if (!visible) return;
        cleanupCache(dataCache.current, currentDateTime);
        const currentHour = currentDateTime.getUTCHours();
        const currentDateStr = formatDateUTC(currentDateTime);
        const prevDateStr = formatDateUTC(subDays(currentDateTime, 1));
        const nextDateStr = formatDateUTC(addDays(currentDateTime, 1));

        const hoursToFetch = [
            { dateStr: currentDateStr, hour: currentHour },
            { dateStr: currentHour === 23 ? nextDateStr : currentDateStr, hour: (currentHour + 1) % 24 },
            { dateStr: currentHour === 0 ? prevDateStr : currentDateStr, hour: (currentHour - 1 + 24) % 24 }
        ];

        try {
            const fetchedData = await Promise.all(
                hoursToFetch.map(async ({ dateStr, hour }) => {
                    const data = await fetchHourData(layerId, dateStr, hour);
                    return data;
                })
            );

            const allDetections = fetchedData.flat().filter(Boolean);
            setContrailDetections(allDetections);
        } catch (error) {
            console.error('Error in fetchRelevantData for GMN detected contrails:', error);
        }
    }, [visible, currentDateTime, fetchHourData, layerId]);

    const debouncedFetchRelevantData = useMemo(
        () => debounce(fetchRelevantData, 300),
        [fetchRelevantData]
    );

    useEffect(() => {
        if (isPlaying) {
            fetchRelevantData();
        } else {
            debouncedFetchRelevantData();
        }

        return () => {
            debouncedFetchRelevantData.cancel();
        };
    }, [fetchRelevantData, debouncedFetchRelevantData, currentDateTime, isPlaying]);

    useEffect(() => {
        const filterDetections = () => {
            const currentDateTimeUnix = currentDateTime.getTime();
            const filtered = contrailDetections.filter((detection) => {
                const detectionTimeUnix = parseISO(detection.timestamp).getTime();
                const timeDiffseconds = (detectionTimeUnix - currentDateTimeUnix) / 1000;
                return timeDiffseconds >= -6 && timeDiffseconds <= 6;
            });
            setFilteredDetections(filtered);
        };

        if (contrailDetections.length > 0) {
            filterDetections();
        } else {
            setFilteredDetections([]);
        }
    }, [contrailDetections, currentDateTime]);

    // Effect to show popup when selectedContrail is set from the parent component
    useEffect(() => {
        if (selectedContrail && selectedContrail.geometry && selectedContrail.geometry.coordinates) {
            // Extract originalData from properties
            let contrailData;
            try {
                if (typeof selectedContrail.properties.originalData === 'string') {
                    contrailData = JSON.parse(selectedContrail.properties.originalData);
                } else {
                    contrailData = selectedContrail.properties;
                }
            } catch (error) {
                console.error('Failed to parse contrail data:', error);
                contrailData = selectedContrail.properties;
            }

            // Find the centroid of the polygon for popup positioning
            const coordinates = selectedContrail.geometry.coordinates[0];
            const centerLng = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length;
            const centerLat = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length;

            setPopupInfo({
                longitude: centerLng,
                latitude: centerLat,
                data: contrailData
            });
        } else {
            setPopupInfo(null);
        }
    }, [selectedContrail]);

    const contrailFeatures = useMemo(() => {
        const features = filteredDetections.map((detection, index) => {
            // Store all detection data as string in properties
            const serializedData = JSON.stringify(detection);
            
            const feature = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [detection.coordinates.map(coord => [coord[1], coord[0]])]  // Swap lat and long
                },
                properties: {
                    id: `gmn-contrail-${index}`,
                    timestamp: detection.timestamp,
                    originalData: serializedData
                },
                id: `gmn-contrail-${index}`
            };
            return feature;
        });
        
        return features;
    }, [filteredDetections]);

    if (!visible) {
        return null;
    }

    return (
        <>
            <Source 
                id={layerId + '-gmn-contrails'} 
                type="geojson"
                data={{ type: 'FeatureCollection', features: contrailFeatures }}
            >
                <Layer
                    id={layerId + '-gmn-contrail-layer'}
                    type="fill"
                    paint={{
                        'fill-color': 'rgba(255, 0, 0, 0.47)',
                        'fill-outline-color': 'rgb(255, 0, 0)',
                        'fill-opacity': 0.7
                    }}
                    // Make layer clickable by adding interactiveLayerIds in parent
                />
                <Layer
                    id={layerId + '-gmn-contrail-outline'}
                    type="line"
                    paint={{
                        'line-color': 'rgb(255, 0, 0)',
                        'line-width': 2
                    }}
                />
                <Layer
                    id={layerId + '-gmn-contrail-fill'}
                    type="fill"
                    paint={{
                        'fill-color': 'rgba(255, 0, 0, 0.01)',
                        'fill-outline-color': 'rgba(0, 0, 0, 0)',
                    }}
                    // This transparent layer helps with interactivity
                />
            </Source>
            
            {/* Popup for contrail data */}
            {popupInfo && (
                <Popup
                    longitude={popupInfo.longitude}
                    latitude={popupInfo.latitude}
                    closeButton={true}
                    closeOnClick={false}
                    onClose={() => setPopupInfo(null)}
                    anchor="bottom"
                    className="gmn-contrail-popup"
                >
                    <div style={{ padding: '10px', maxWidth: '300px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>Contrail Details</h3>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <pre style={{ fontSize: '12px', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', overflow: 'auto' }}>
                                {JSON.stringify(popupInfo.data, null, 2)}
                            </pre>
                        </div>
                        {typeof ContrailFrame !== 'undefined' && (
                            <ContrailFrame
                                currentDateTime={currentDateTime}
                                visible={true}
                                contrailData={popupInfo.data}
                            />
                        )}
                    </div>
                </Popup>
            )}
        </>
    );
};

export default GmnContrailLayer;